import { Header, flexRender } from '@tanstack/react-table'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical } from 'lucide-react'
import * as React from 'react'

import { cn } from '@/lib/utils'
import { TableHead } from './table'

interface HeaderCellProps {
  header: Header<any, any>
  enableColumnReordering: boolean
  enableColumnResizing: boolean
  isColumnSortable?: boolean
}

export const HeaderCell = React.memo(({ header, enableColumnReordering, enableColumnResizing, isColumnSortable = true }: HeaderCellProps) => {
  const sortableProps = useSortable({ id: header.id })

  return (
    <TableHead
      ref={enableColumnReordering ? sortableProps.setNodeRef : undefined}
      className="relative box-border overflow-hidden"
      style={{
        width: enableColumnResizing ? header.getSize() : 'auto',
        transform: enableColumnReordering ? CSS.Transform.toString(sortableProps.transform) : undefined,
        transition: enableColumnReordering ? sortableProps.transition : undefined,
        opacity: enableColumnReordering && sortableProps.isDragging ? 0.5 : 1,
        zIndex: enableColumnReordering && sortableProps.isDragging ? 10 : 'auto',
      }}
    >
      {enableColumnReordering && isColumnSortable && (
        <button
          type="button"
          className={cn(
            'absolute left-0 top-0 flex h-full items-center justify-center p-1 opacity-0 transition-opacity hover:opacity-100 focus:opacity-100',
            'cursor-grab active:cursor-grabbing',
            'text-muted-foreground hover:text-foreground z-10'
          )}
          {...Object.fromEntries(
            Object.entries(sortableProps.attributes).filter(([key]) => key !== 'visible')
          )}
          {...sortableProps.listeners}
        >
          <GripVertical className="h-4 w-4" />
        </button>
      )}
      <div className={cn('flex items-center', enableColumnReordering ? 'pl-6' : '')}>
        {header.isPlaceholder
          ? null
          : flexRender(header.column.columnDef.header, header.getContext())}
      </div>
      {enableColumnResizing && header.column.getCanResize() && !(enableColumnReordering && sortableProps.isDragging) && (
        <div
          onMouseDown={header.getResizeHandler()}
          onTouchStart={header.getResizeHandler()}
          className="absolute right-0 top-0 -mr-2 flex h-full w-4 cursor-col-resize touch-none select-none items-center justify-center z-10"
        >
          <div
            className={`h-full transition-all ${
              header.column.getIsResizing()
                ? 'bg-primary w-1'
                : 'bg-border/40 hover:bg-primary/60 w-[2px] hover:w-1'
            }`}
          />
        </div>
      )}
    </TableHead>
  )
})

HeaderCell.displayName = 'HeaderCell'